import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { storageService } from '../services/storageService';
import FileUpload from '../components/FileUpload';
import SEO from '../components/SEO';

const HowToUploadPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [uploadStatus, setUploadStatus] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const handleManualUpload = async (file: File) => {
      if (!file || !currentUser) return;

      try {
          await storageService.uploadPendingRaceFile(file, currentUser.uid, setUploadStatus);
      } catch (error) {
          console.error('Manual upload failed:', error);
          setUploadStatus({ message: error instanceof Error ? error.message : 'Upload failed. Please try again.', type: 'error' });
          throw error;
      }
  };

  return (
    <div className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
      <SEO title="How to Upload | RaceLineIQ" />
      <div className="max-w-7xl mx-auto">
        
        {/* Sync & Upload Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* Automatic Sync Section */}
            <div className="bg-gray-800 rounded-xl p-8 md:p-12 border border-cyan-500/30 shadow-lg text-center relative overflow-hidden flex flex-col justify-center items-center">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                <h2 className="text-3xl font-bold text-white mb-4">Automatic Sync (Recommended)</h2>
                <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-4">
                    Connect your Wahoo or Hammerhead account (Garmin coming soon!) to automatically push your race files to RaceLineIQ. It's the fastest way to get your data analyzed.
                </p>
                <p className="text-sm text-gray-400 max-w-2xl mx-auto mb-8">
                    Raceline IQ will only pull activities from events in our catalog, all other activities will be ignored. We don't want your 14 min Zwift ride. Sorry, not sorry.
                </p>
                <Link 
                    to="/profile" 
                    className="inline-flex items-center px-8 py-3 text-lg font-bold rounded-lg text-gray-900 bg-cyan-400 hover:bg-cyan-300 transition-colors shadow-[0_0_15px_rgba(34,211,238,0.3)]"
                >
                    Connect Account
                </Link>
            </div>

            {/* Manual Upload Section */}
            <div className="bg-gray-800/40 rounded-xl p-8 md:p-12 border border-slate-600/50 shadow-sm flex flex-col justify-center text-center">
                <h3 className="text-3xl font-bold text-white mb-4">Manual Upload</h3>
                <p className="text-lg text-white mb-8">
                    Upload a .FIT, .GPX, or .TCX file directly. We'll automatically match it to the correct race and category.
                </p>
                
                {uploadStatus && (
                    <div className={`p-3 rounded-lg text-sm mb-4 ${uploadStatus.type === 'success' ? 'bg-green-900/30 text-green-400 border border-green-800/50' : uploadStatus.type === 'error' ? 'bg-red-900/30 text-red-400 border border-red-800/50' : 'bg-slate-700/50 text-slate-300 border border-slate-600/50'}`}>
                        {uploadStatus.message}
                    </div>
                )}
                
                <div className="w-full max-w-md mx-auto">
                    <FileUpload onFileUpload={handleManualUpload} label="Upload File" />
                </div>
            </div>
        </div>

        <div className="text-center mb-12 border-t border-gray-800 pt-12">
            <h1 className="text-2xl font-bold text-gray-400 mb-4">Manual .FIT File Download Guide</h1>
            <p className="text-base text-gray-400 max-w-3xl mx-auto">
                The .FIT (Flexible and Interoperable Data Transfer) file format is the universal standard for activity data. The processes below detail how to manually retrieve this original file from your device.
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Garmin Column */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 prose prose-invert prose-lg max-w-none">
                <h2 className="text-3xl font-semibold text-cyan-500 !mb-4">Garmin</h2>
                <p className="text-sm">The simplest method is via the Garmin Connect web platform.</p>
                
                <h3 className="text-xl font-semibold !mt-6 !mb-2">Method A: Garmin Connect Web</h3>
                <ol className="text-sm !space-y-2 !pl-5 text-gray-300">
                    <li><strong>Log In:</strong> Go to the <a href="https://connect.garmin.com/" target="_blank" rel="noopener noreferrer">Garmin Connect</a> site.</li>
                    <li><strong>Activities:</strong> Go to <em>Menu &gt; All Activities</em>.</li>
                    <li><strong>Select Activity:</strong> Click on your desired activity.</li>
                    <li><strong>Export:</strong> Click the <strong>Gear Icon</strong> and select <strong>"Export Original"</strong>.</li>
                </ol>

                <h3 className="text-xl font-semibold !mt-6 !mb-2">Method B: Direct USB Access</h3>
                <ol className="text-sm !space-y-2 !pl-5 text-gray-300">
                    <li><strong>Connect:</strong> Plug your device into a computer via USB.</li>
                    <li><strong>Browse:</strong> Open the mounted drive and go to the <code>Garmin/Activity</code> folder.</li>
                    <li><strong>Copy:</strong> Find the file (e.g., <code>2025-12-15-12-00-00.fit</code>) and copy it to your computer.</li>
                </ol>
            </div>

            {/* Wahoo Column */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 prose prose-invert prose-lg max-w-none">
                <h2 className="text-3xl font-semibold text-cyan-500 !mb-4">Wahoo</h2>
                <p className="text-sm">Wahoo has no web portal; direct access is most reliable.</p>
                
                <h3 className="text-xl font-semibold !mt-6 !mb-2">Method A: Direct USB Access</h3>
                <ol className="text-sm !space-y-2 !pl-5 text-gray-300">
                    <li><strong>Connect:</strong> Plug your ELEMNT, BOLT, or ROAM into a computer via USB.</li>
                    <li><strong>Browse:</strong> The device mounts as storage. Locate the <code>activities</code> folder.</li>
                    <li><strong>Copy:</strong> Find your .fit file and copy it to your computer.</li>
                </ol>

                <h3 className="text-xl font-semibold !mt-6 !mb-2">Method B: Mobile App Email</h3>
                <ol className="text-sm !space-y-2 !pl-5 text-gray-300">
                    <li><strong>Open App:</strong> In the Wahoo ELEMNT app, go to the <em>History</em> tab.</li>
                    <li><strong>Select Ride:</strong> Choose the desired ride.</li>
                    <li><strong>Share:</strong> Find the Share icon and choose <strong>"Email .fit File"</strong>.</li>
                </ol>
            </div>

            {/* Hammerhead Column */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 prose prose-invert prose-lg max-w-none">
                <h2 className="text-3xl font-semibold text-cyan-500 !mb-4">Hammerhead</h2>
                <p className="text-sm">Hammerhead offers both a simple web export and direct access.</p>
                
                <h3 className="text-xl font-semibold !mt-6 !mb-2">Method A: Hammerhead Dashboard</h3>
                <ol className="text-sm !space-y-2 !pl-5 text-gray-300">
                    <li><strong>Log In:</strong> Go to the <a href="https://dashboard.hammerhead.io/" target="_blank" rel="noopener noreferrer">Hammerhead Dashboard</a>.</li>
                    <li><strong>Rides:</strong> Navigate to the <em>Rides</em> section.</li>
                    <li><strong>Select Ride:</strong> Click on the specific ride.</li>
                    <li><strong>Download:</strong> Find the <em>Download</em> button and select the .fit format.</li>
                </ol>

                <h3 className="text-xl font-semibold !mt-6 !mb-2">Method B: Direct USB Access (MTP)</h3>
                <ol className="text-sm !space-y-2 !pl-5 text-gray-300">
                    <li><strong>Connect:</strong> Plug your Karoo into a computer via USB.</li>
                    <li><strong>Enable Transfer:</strong> On the Karoo screen, select the <strong>"File Transfer"</strong> or "MTP" option.</li>
                    <li><strong>Browse:</strong> The device will appear as storage. Navigate to the <code>Karoo/Activities</code> folder.</li>
                    <li><strong>Copy:</strong> Copy the desired .fit file to your computer.</li>
                </ol>
            </div>
        </div>

        <div className="mt-12 border-t border-gray-600 pt-8 text-center max-w-3xl mx-auto">
            <h3 className="text-xl font-semibold text-cyan-400">Note on File Integrity</h3>
            <p className="text-gray-400 mt-2">
                For critical data, always ensure you download the <strong>Original</strong> file. Some platforms offer exports in .gpx or .tcx formats, which can occasionally truncate or simplify complex data (like running dynamics or sensor data).
            </p>
        </div>
      </div>
    </div>
  );
};

export default HowToUploadPage;
